import type { CameraActivityMeasurementResponse } from '../../services/cameraActivityMeasurementApi';

const SUBJECT = {
  site_id: 'yyz',
  device_id: 'demo5',
  camera_id: 'cam_walkway_zone_demo',
  scope: 'CAMERA' as const,
  zone_id: null as string | null,
  zone_version: null as number | null,
};

const WINDOW = {
  start: '2026-08-21T18:00:00.000Z',
  end: '2026-08-21T19:00:00.000Z',
};

function metric(state: string, seconds: number | null, share: number | null) {
  return { state, seconds, share };
}

export function makeCameraMeasurementResponse(
  overrides: Omit<Partial<CameraActivityMeasurementResponse>, 'metrics' | 'quality' | 'subject' | 'provenance'> & {
    metrics?: Partial<CameraActivityMeasurementResponse['metrics']>;
    quality?: Partial<CameraActivityMeasurementResponse['quality']>;
    subject?: Partial<CameraActivityMeasurementResponse['subject']>;
    provenance?: Partial<CameraActivityMeasurementResponse['provenance']>;
  } = {},
): CameraActivityMeasurementResponse {
  const { metrics, quality, subject, provenance, ...rest } = overrides;
  return {
    ok: true,
    subject: { ...SUBJECT, ...subject },
    window: WINDOW,
    state: 'MEASURED',
    claim_eligible: true,
    reason_codes: [],
    metrics: {
      pedestrian_presence: metric('MEASURED', 8040, 0.09314),
      forklift_presence: metric('MEASURED', 3780, 0.044),
      simultaneous_presence: metric('MEASURED', 660, 0.008),
      ...metrics,
    },
    quality: {
      usable_camera_seconds: 3598,
      source_observed_seconds: 3599,
      producer_completeness: 0.99944,
      producer_gap_seconds: 2,
      coverage_complete: true,
      activity_complete: true,
      source_compatible: true,
      ...quality,
    },
    provenance: {
      measurement_definition_version: 'camera_activity_v1',
      source_signature: 'sig-should-not-render',
      producer_version: 'camera_activity_accumulator_v1',
      ...provenance,
    },
    ...rest,
  };
}

export const CAMERA_MEASUREMENT_GOLDENS = {
  measured: makeCameraMeasurementResponse(),
  measuredZero: makeCameraMeasurementResponse({
    metrics: {
      pedestrian_presence: metric('MEASURED', 0, 0),
      forklift_presence: metric('MEASURED', 0, 0),
      simultaneous_presence: metric('MEASURED', 0, 0),
    },
  }),
  limitedProducerGap: makeCameraMeasurementResponse({
    state: 'LIMITED',
    claim_eligible: false,
    reason_codes: ['READING_SOURCE_GAP'],
    metrics: {
      pedestrian_presence: metric('LIMITED', 100, null),
      forklift_presence: metric('LIMITED', 40, null),
      simultaneous_presence: metric('LIMITED', 20, null),
    },
    quality: {
      producer_completeness: 0.8,
      usable_camera_seconds: 2800,
      source_observed_seconds: 2240,
      producer_gap_seconds: 560,
    },
  }),
  limitedObservedWalkway: makeCameraMeasurementResponse({
    state: 'LIMITED',
    claim_eligible: false,
    reason_codes: ['READING_SOURCE_GAP', 'COVERAGE_NOT_FINAL', 'PARTIAL_WINDOW'],
    metrics: {
      pedestrian_presence: metric('LIMITED', 657.5230479742969, null),
      forklift_presence: metric('LIMITED', 158.8746377015268, null),
      simultaneous_presence: metric('LIMITED', 147.5815123121065, null),
    },
    quality: {
      usable_camera_seconds: 900,
      source_observed_seconds: 866.0098854202953,
      producer_completeness: 0.962233,
      producer_gap_seconds: 33.99011457970471,
      coverage_complete: false,
      activity_complete: false,
    },
  }),
  limitedCoverageNoProducer: makeCameraMeasurementResponse({
    state: 'LIMITED',
    claim_eligible: false,
    reason_codes: ['READING_SOURCE_GAP', 'COVERAGE_NOT_FINAL', 'PARTIAL_WINDOW'],
    metrics: {
      pedestrian_presence: metric('LIMITED', null, null),
      forklift_presence: metric('LIMITED', null, null),
      simultaneous_presence: metric('LIMITED', null, null),
    },
    quality: {
      usable_camera_seconds: 856.8,
      source_observed_seconds: null,
      producer_completeness: null,
      producer_gap_seconds: 856.8,
      coverage_complete: false,
      activity_complete: false,
    },
  }),
  notMeasuredTelemetry: makeCameraMeasurementResponse({
    state: 'NOT_MEASURED',
    claim_eligible: false,
    reason_codes: ['TELEMETRY_MISSING'],
    metrics: {
      pedestrian_presence: metric('NOT_MEASURED', null, null),
      forklift_presence: metric('NOT_MEASURED', null, null),
      simultaneous_presence: metric('NOT_MEASURED', null, null),
    },
    quality: {
      usable_camera_seconds: null,
      producer_completeness: null,
    },
  }),
  unsupportedForklift: makeCameraMeasurementResponse({
    state: 'MEASURED',
    metrics: {
      pedestrian_presence: metric('MEASURED', 8040, 0.09314),
      forklift_presence: metric('UNSUPPORTED', null, null),
      simultaneous_presence: metric('UNSUPPORTED', null, null),
    },
  }),
  deviceScopeUnresolved: makeCameraMeasurementResponse({
    state: 'LIMITED',
    claim_eligible: false,
    reason_codes: ['device_scope_unresolved'],
    metrics: {
      pedestrian_presence: metric('LIMITED', null, null),
      forklift_presence: metric('LIMITED', null, null),
      simultaneous_presence: metric('LIMITED', null, null),
    },
  }),
  mixedSourceVersion: makeCameraMeasurementResponse({
    state: 'LIMITED',
    claim_eligible: false,
    reason_codes: ['INCOMPATIBLE_SOURCE_VERSION', 'SOURCE_SIGNATURE_MIXED'],
    metrics: {
      pedestrian_presence: metric('LIMITED', null, null),
      forklift_presence: metric('LIMITED', null, null),
      simultaneous_presence: metric('LIMITED', null, null),
    },
  }),
  zone: makeCameraMeasurementResponse({
    subject: {
      ...SUBJECT,
      scope: 'ZONE',
      zone_id: 'worksafety',
      zone_version: 3,
    },
  }),
  unalignedLimited: makeCameraMeasurementResponse({
    state: 'LIMITED',
    claim_eligible: false,
    reason_codes: ['PARTIAL_WINDOW'],
    metrics: {
      pedestrian_presence: metric('LIMITED', null, null),
      forklift_presence: metric('LIMITED', null, null),
      simultaneous_presence: metric('LIMITED', null, null),
    },
  }),
  /** Backend ACTIVITY_DATA_CONFLICT when O > U; completeness O/U > 1 must not clamp to 100%. */
  limitedObservedExceedsUsable: makeCameraMeasurementResponse({
    state: 'LIMITED',
    claim_eligible: false,
    reason_codes: ['COVERAGE_NOT_FINAL', 'PARTIAL_WINDOW', 'ACTIVITY_DATA_CONFLICT'],
    metrics: {
      pedestrian_presence: metric('LIMITED', 657.5230479742969, null),
      forklift_presence: metric('LIMITED', 158.8746377015268, null),
      simultaneous_presence: metric('LIMITED', 147.5815123121065, null),
    },
    quality: {
      usable_camera_seconds: 403,
      source_observed_seconds: 879,
      producer_completeness: 879 / 403,
      producer_gap_seconds: 0,
      coverage_complete: false,
      activity_complete: false,
    },
  }),
};
